// Cloud login — social OAuth primary, passkey secondary, email-OTP optional.
//
// Design register: Linear/Vercel centered single-column card on a solid surface
// (frontend-design "Linear-Precision" / "System UI"). Restraint over decoration:
// ONE accent (primary) on the single most-likely action, everything else neutral.
// No marketing copy — returning users already chose the product.
//
// Method visibility is server-driven: on mount we fetch /api/auth-methods (the
// config-derived source of truth) and render ONLY the methods that will actually
// work. A deploy with no AgentMail transport shows OAuth + passkey and hides the
// email path entirely — no dead UI, no dependency the operator must configure.
//
// Auth model (workers/cloud/src/auth.ts): GitHub/Google OAuth is the transport-
// free primary sign-in; passkeys are the strong credential a user adds; email-OTP
// is an optional bootstrap/recovery path. The clientId never touches the browser —
// authClient.signIn.social() hits our Worker, which redirects to the provider.
//
// Accessibility (WCAG 2.2): visible labels (never placeholder-only), a single
// pasteable OTP input with autocomplete="one-time-code" (SC 3.3.8 — no cognitive
// test / paste supported), aria-live error + status regions, focus moved to the
// code field on step change, and ≥h-10 (40px) targets.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button } from '@onestone/ui/button';
import { Input } from '@onestone/ui/input';
import { Label } from '@onestone/ui/label';
import { authClient } from './authClient';

type Step = 'email' | 'code';
type SocialProvider = 'github' | 'google';

interface AuthMethods {
  github: boolean;
  google: boolean;
  emailOtp: boolean;
  passkey: boolean;
}

const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;

/** Mask a local-part for the "sent to …" confirmation (j•••@gmail.com). */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const head = local.slice(0, 1);
  return `${head}${'•'.repeat(Math.max(1, local.length - 1))}@${domain}`;
}

/** Better Auth client error shape ({ data, error } convention — never throws). */
type ClientError = { message?: string | undefined; code?: string | undefined } | null | undefined;

/** Human message from a Better Auth client error. */
function errText(error: ClientError, fallback: string): string {
  return error?.message?.trim() || fallback;
}

// ─── Provider marks (inline — no icon dependency) ─────────────────────────────
function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}
function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

const PROVIDER_META: Record<SocialProvider, { label: string; Icon: () => JSX.Element }> = {
  github: { label: 'Continue with GitHub', Icon: GitHubIcon },
  google: { label: 'Continue with Google', Icon: GoogleIcon },
};

export function CloudLogin() {
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resendIn, setResendIn] = useState(0);

  const emailId = useId();
  const otpId = useId();
  const codeRef = useRef<HTMLInputElement>(null);

  // Fetch the enabled sign-in methods (same-origin). On failure, fall back to
  // passkey-only so a config hiccup never locks a returning user out.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/auth-methods', { credentials: 'include' });
        if (!res.ok) throw new Error(`auth-methods ${res.status}`);
        const data = (await res.json()) as AuthMethods;
        if (!cancelled) setMethods(data);
      } catch {
        if (!cancelled) setMethods({ github: false, google: false, emailOtp: false, passkey: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Passkey Conditional UI: sits silent until the user taps a passkey in the
  // browser's autofill (attaches to the email field's autocomplete="username
  // webauthn"). Guarded — unsupported browsers simply never resolve the mediation.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const supported = await (
          window.PublicKeyCredential as unknown as {
            isConditionalMediationAvailable?: () => Promise<boolean>;
          }
        )?.isConditionalMediationAvailable?.();
        if (cancelled || !supported) return;
        await authClient.signIn.passkey({ autoFill: true });
        // On success the session store updates and CloudAuthGate swaps to the app.
      } catch {
        /* conditional UI unsupported or cancelled — no-op */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resend countdown.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // Move focus to the code field when the step advances (announces new context).
  useEffect(() => {
    if (step !== 'code') return undefined;
    const t = setTimeout(() => codeRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [step]);

  const signInWithProvider = useCallback(async (provider: SocialProvider) => {
    setError('');
    setBusy(true);
    // Full-page redirect to the provider on success; only returns here on error.
    const { error: sErr } = await authClient.signIn.social({ provider, callbackURL: '/' });
    if (sErr) {
      setBusy(false);
      setError(errText(sErr, `Could not start ${PROVIDER_META[provider].label}. Try again.`));
    }
  }, []);

  const signInWithPasskey = useCallback(async () => {
    setError('');
    setBusy(true);
    const res = await authClient.signIn.passkey();
    setBusy(false);
    // Passkey methods always return { data, error } (never throw). A user
    // cancelling the OS dialog surfaces as an error we quietly ignore.
    if (res?.error) {
      const code = (res.error as { code?: string }).code ?? '';
      if (!/cancel|abort|not.?allowed/i.test(`${code} ${res.error.message ?? ''}`)) {
        setError(errText(res.error, 'Passkey sign-in failed. Try another method.'));
      }
    }
  }, []);

  const sendCode = useCallback(async () => {
    setError('');
    setNotice('');
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email to continue.');
      return;
    }
    setBusy(true);
    const { error: sErr } = await authClient.emailOtp.sendVerificationOtp({ email: trimmed, type: 'sign-in' });
    setBusy(false);
    if (sErr) {
      setError(errText(sErr, 'Could not send a code. Try again.'));
      return;
    }
    setStep('code');
    setResendIn(RESEND_SECONDS);
    setNotice(`We sent a 6-digit code to ${maskEmail(trimmed)}.`);
  }, [email]);

  const verifyCode = useCallback(async () => {
    setError('');
    if (otp.length !== OTP_LENGTH) {
      setError(`Enter the ${OTP_LENGTH}-digit code.`);
      return;
    }
    setBusy(true);
    const { error: vErr } = await authClient.signIn.emailOtp({ email: email.trim(), otp });
    setBusy(false);
    if (vErr) {
      setOtp('');
      setError(errText(vErr, "That code doesn't match. Try again."));
      codeRef.current?.focus();
      return;
    }
    // Success: session store updates → CloudAuthGate renders the app.
  }, [email, otp]);

  const backToEmail = useCallback(() => {
    setStep('email');
    setOtp('');
    setError('');
    setNotice('');
  }, []);

  // Ordered social providers that are actually enabled.
  const providers: SocialProvider[] = methods ? (['github', 'google'] as const).filter((p) => methods[p]) : [];
  // ONE accent: the first OAuth provider, or the passkey button when no OAuth.
  const passkeyIsPrimary = providers.length === 0;

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src="/logo-256.png" alt="" className="h-10 w-10 rounded-lg shadow-sm" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">Sign in to OneStone</h1>
            <p className="text-sm text-muted-foreground">Choose how you&rsquo;d like to continue.</p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          {methods === null ? (
            <div className="flex items-center justify-center py-8" aria-busy="true" aria-live="polite">
              <span className="sr-only">Loading sign-in options…</span>
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground motion-reduce:animate-none" />
            </div>
          ) : step === 'email' ? (
            <div className="flex flex-col gap-4">
              {/* Social OAuth — primary, transport-free. */}
              {providers.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {providers.map((p, i) => {
                    const { label, Icon } = PROVIDER_META[p];
                    return (
                      <Button
                        key={p}
                        type="button"
                        variant={i === 0 ? 'default' : 'outline'}
                        onClick={() => void signInWithProvider(p)}
                        disabled={busy}
                        className="h-10 w-full"
                      >
                        <Icon />
                        {label}
                      </Button>
                    );
                  })}
                </div>
              ) : null}

              {/* Passkey — strong credential for returning users. */}
              <Button
                type="button"
                variant={passkeyIsPrimary ? 'default' : 'outline'}
                onClick={() => void signInWithPasskey()}
                disabled={busy}
                className="h-10 w-full"
              >
                Sign in with a passkey
              </Button>

              {/* Email-OTP — optional recovery path, only when configured. */}
              {methods.emailOtp ? (
                <>
                  <div className="relative py-1 text-center">
                    <span className="relative z-10 bg-card px-2 text-xs text-muted-foreground">or email me a code</span>
                    <span
                      className="absolute inset-x-0 top-1/2 -z-0 h-px -translate-y-1/2 bg-border"
                      aria-hidden="true"
                    />
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void sendCode();
                    }}
                    className="flex flex-col gap-3"
                    noValidate
                  >
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={emailId} className="text-sm font-medium">
                        Email
                      </Label>
                      <Input
                        id={emailId}
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="username webauthn"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="email"
                        placeholder="you@example.com"
                        disabled={busy}
                        className="h-10"
                      />
                    </div>
                    <Button type="submit" variant="outline" disabled={busy} className="h-10 w-full">
                      {busy ? 'Sending code…' : 'Email me a sign-in code'}
                    </Button>
                  </form>
                </>
              ) : null}
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void verifyCode();
              }}
              className="flex flex-col gap-4"
              noValidate
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={otpId} className="text-sm font-medium">
                  Enter the 6-digit code
                </Label>
                <Input
                  ref={codeRef}
                  id={otpId}
                  name="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={OTP_LENGTH}
                  disabled={busy}
                  className="h-11 text-center text-lg font-medium tracking-[0.4em]"
                />
              </div>

              <Button type="submit" disabled={busy || otp.length !== OTP_LENGTH} className="h-10 w-full">
                {busy ? 'Verifying…' : 'Verify & sign in'}
              </Button>

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={backToEmail}
                  className="rounded text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={() => void sendCode()}
                  disabled={resendIn > 0 || busy}
                  className="rounded text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {resendIn > 0 ? `Resend in 0:${String(resendIn).padStart(2, '0')}` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          {/* Status + error live regions — announced to assistive tech. */}
          <p className="mt-3 min-h-[1rem] text-center text-xs text-muted-foreground" aria-live="polite">
            {notice}
          </p>
          {error ? (
            <p className="mt-1 text-center text-xs text-destructive" aria-live="assertive">
              {error}
            </p>
          ) : null}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground/80">
          Protected by passkeys. We never store a password.
        </p>
      </div>
    </main>
  );
}
